import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Loader2, Save, FileSpreadsheet, RotateCcw, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { api, Store, Staff, CsvColumnConfig } from '../../api';
import { useToast } from '../../contexts/ToastContext';

const DEFAULT_CSV_CONFIG: CsvColumnConfig[] = [
    { id: 'date', label: '日付', enabled: true },
    { id: 'staffName', label: 'スタッフ名', enabled: true },
    { id: 'staffId', label: 'スタッフID', enabled: true },
    { id: 'startTime', label: '開始時刻', enabled: true },
    { id: 'endTime', label: '終了時刻', enabled: true },
    { id: 'breakMinutes', label: '休憩（分）', enabled: true },
    { id: 'workHours', label: '実働（時間）', enabled: true },
    { id: 'salary', label: '概算給与', enabled: true },
    { id: 'transportFee', label: '交通費', enabled: true },
    { id: 'employmentType', label: '雇用形態', enabled: true },
    { id: 'note', label: '備考', enabled: true },
];

interface CsvSettingsTabProps {
  stores: Store[];
  myProfile: Staff | null;
  isSystemAdmin: boolean;
  onStoresChange?: (stores: Store[]) => void;
}

const CsvSettingsTab: React.FC<CsvSettingsTabProps> = ({ stores, myProfile, isSystemAdmin, onStoresChange }) => {
  const { showToast } = useToast();
  const [csvConfigStoreId, setCsvConfigStoreId] = useState<string>('');
  const [currentCsvConfig, setCurrentCsvConfig] = useState<CsvColumnConfig[]>(DEFAULT_CSV_CONFIG);
  const [isLoading, setIsLoading] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (idx: number) => {
    setDragIndex(idx);
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIndex(idx);
  };

  const handleDrop = (idx: number) => {
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const newConfig = [...currentCsvConfig];
    const [moved] = newConfig.splice(dragIndex, 1);
    newConfig.splice(idx, 0, moved);
    setCurrentCsvConfig(newConfig);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const labelClass = "text-xs font-bold text-slate-500 mb-1.5 block uppercase tracking-wider";

  useEffect(() => {
    const myStoreIds = myProfile?.stores || [];
    const availableStores = stores.filter(s => isSystemAdmin || myStoreIds.includes(s.id));
    if (availableStores.length > 0 && !csvConfigStoreId) {
      setCsvConfigStoreId(availableStores[0].id);
      setCurrentCsvConfig(availableStores[0].csvConfig || DEFAULT_CSV_CONFIG);
    }
  }, [stores, myProfile]);

  const handleSave = async () => {
    if (!csvConfigStoreId) return;
    setIsLoading(true);
    try {
      const targetStore = stores.find(s => s.id === csvConfigStoreId);
      if (!targetStore) return;
      const updatedStore = { ...targetStore, csvConfig: currentCsvConfig };
      await api.saveStores([updatedStore]);
      const updatedStores = stores.map(s => s.id === csvConfigStoreId ? updatedStore : s);
      onStoresChange?.(updatedStores);
      showToast('保存しました。', 'success');
    } catch (e) {
      showToast('保存に失敗しました。', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setCurrentCsvConfig(DEFAULT_CSV_CONFIG);
    showToast('標準設定を読み込みました（保存が必要です）。', 'info');
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newConfig = [...currentCsvConfig];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newConfig.length) return;
    [newConfig[index], newConfig[targetIndex]] = [newConfig[targetIndex], newConfig[index]];
    setCurrentCsvConfig(newConfig);
  };

  const updateItem = (id: string, updates: Partial<CsvColumnConfig>) => {
    setCurrentCsvConfig(prev => prev.map(item => item.id === id ? { ...item, ...updates } : item));
  };

  const addCustomItem = () => {
    const newCol: CsvColumnConfig = { id: `custom_${Date.now()}`, label: '新規項目', enabled: true, fixedValue: '' };
    setCurrentCsvConfig(prev => [...prev, newCol]);
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <section className="bg-white p-8 border border-gray-100 shadow-sm rounded-[4px]">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-lg font-bold text-slate-800 border-l-4 border-blue-600 pl-4 uppercase">CSVカスタマイズ</h2>
            <p className="text-xs text-slate-500 mt-1 pl-4">給与ソフト等に合わせて出力項目を調整できます。</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleReset} className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-2 rounded flex items-center gap-1"><RotateCcw size={14} /> 標準に戻す</button>
            <button onClick={handleSave} disabled={isLoading} className="bg-blue-600 text-white font-bold px-6 py-2 rounded text-sm flex items-center gap-2">
              {isLoading ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} 保存
            </button>
          </div>
        </div>

        <div className="mb-4">
          <label className={labelClass}>設定対象店舗</label>
          <select value={csvConfigStoreId} onChange={(e) => {
            setCsvConfigStoreId(e.target.value);
            const store = stores.find(s => s.id === e.target.value);
            setCurrentCsvConfig(store?.csvConfig || DEFAULT_CSV_CONFIG);
          }} className="w-64 bg-white border border-slate-300 rounded-[4px] px-3 py-2 text-sm font-bold">
            {stores.filter(s => isSystemAdmin || myProfile?.stores?.includes(s.id)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        {/* Preview */}
        <div className="mt-4 mb-8 bg-slate-50 border border-slate-200 rounded-lg p-4 overflow-x-auto">
          <h4 className="text-[10px] font-bold text-slate-400 uppercase mb-3 flex items-center gap-2"><FileSpreadsheet size={14} /> CSV出力プレビュー</h4>
          <div className="inline-block min-w-full bg-white border border-slate-200 rounded overflow-hidden">
            <div className="flex border-b border-slate-200 bg-slate-100/50">
              {currentCsvConfig.filter(c => c.enabled).map(c => <div key={c.id} className="px-4 py-2 text-[10px] font-bold text-slate-600 border-r border-slate-200 min-w-[100px] max-w-[200px] truncate" title={c.label}>{c.label}</div>)}
            </div>
            <div className="flex">
              {currentCsvConfig.filter(c => c.enabled).map(c => {const val = c.id.startsWith('custom_') ? (c.fixedValue || '（空）') : 'サンプル'; return <div key={c.id} className="px-4 py-2 text-[10px] text-slate-400 border-r border-slate-200 min-w-[100px] max-w-[200px] truncate font-mono" title={val}>{val}</div>;})}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-3">
          <h3 className="text-xs font-bold text-slate-400 uppercase">出力項目一覧</h3>
          <button onClick={addCustomItem} className="text-[10px] font-bold text-blue-600 border border-blue-200 px-3 py-1.5 rounded flex items-center gap-1.5"><Plus size={12} /> 固定値項目を追加</button>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead className="bg-white text-slate-400 font-bold border-b border-slate-200 text-[10px] uppercase">
              <tr><th className="p-4 w-12">出力</th><th className="p-4 w-10"></th><th className="p-4 w-24 text-center">並び順</th><th className="p-4 w-32">種別</th><th className="p-4">ヘッダー名</th><th className="p-4">固定値</th><th className="p-4 w-12"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {currentCsvConfig.map((item, idx) => (
                <tr key={item.id} draggable onDragStart={() => handleDragStart(idx)} onDragOver={(e) => handleDragOver(e, idx)} onDrop={() => handleDrop(idx)} onDragEnd={handleDragEnd} className={`transition-colors ${dragOverIndex === idx ? 'border-t-2 border-blue-400' : ''} ${dragIndex === idx ? 'opacity-40' : ''} ${item.enabled ? 'bg-white' : 'bg-slate-50'}`}>
                  <td className="p-4"><button onClick={() => updateItem(item.id, { enabled: !item.enabled })} className={`w-10 h-6 rounded-full relative ${item.enabled ? 'bg-blue-600' : 'bg-slate-300'}`}><div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${item.enabled ? 'right-1' : 'left-1'}`} /></button></td>
                  <td className="p-4 cursor-grab active:cursor-grabbing"><GripVertical size={16} className="text-slate-300" /></td>
                  <td className="p-4 text-center"><div className="flex gap-1 justify-center"><button onClick={() => moveItem(idx, 'up')} disabled={idx === 0} className="p-1.5 text-slate-300 hover:text-blue-600 disabled:opacity-0"><ArrowUp size={14}/></button><button onClick={() => moveItem(idx, 'down')} disabled={idx === currentCsvConfig.length - 1} className="p-1.5 text-slate-300 hover:text-blue-600 disabled:opacity-0"><ArrowDown size={14}/></button></div></td>
                  <td className="p-4 text-[10px] font-bold">{item.id.startsWith('custom_') ? <span className="text-blue-600">カスタム</span> : <span className="text-slate-400">標準</span>}</td>
                  <td className="p-4"><input type="text" value={item.label} disabled={!item.enabled} onChange={(e) => updateItem(item.id, { label: e.target.value })} className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm font-bold" /></td>
                  <td className="p-4">{item.id.startsWith('custom_') ? <input type="text" value={item.fixedValue || ''} onChange={(e) => updateItem(item.id, { fixedValue: e.target.value })} className="w-full border border-slate-200 rounded px-3 py-1.5 text-xs font-mono" /> : <span className="text-[10px] text-slate-300 italic">勤怠データから自動取得</span>}</td>
                  <td className="p-4">{item.id.startsWith('custom_') && <button onClick={() => setCurrentCsvConfig(prev => prev.filter(c => c.id !== item.id))} className="text-slate-300 hover:text-red-500"><Trash2 size={16} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default CsvSettingsTab;